import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import Loader from '@zeus/core/Loader';
import { MetricsGraphs, MetricsTS } from '_/components/common/MetricsGraphs/MetricsGraphs';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useSourceContext } from '_/contexts/SourceContext';
import zeusSdk from '_/utils/SDKs';
import React, { ReactElement, useEffect, useState } from 'react';

function SourceGraphs(): ReactElement {
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const { selectedCollection, selectedCollectionSlug } = useCollection();
  const { selectedSource, selectedSourceSlug } = useSourceContext();
  const { setBreadcrumbs } = useTopHeader();
  const [metricsTS, setMetricsTS] = useState<MetricsTS | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
    ];

    if (selectedCollection) {
      breadcrumbs.push({
        title: selectedCollection.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/home`,
      });
    }

    if (selectedSource) {
      breadcrumbs.push(
        {
          title: 'Sources',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources`,
        },
        {
          title: selectedSource.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/home`,
        },
        { title: 'Graphs' },
      );
    }

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    setBreadcrumbs,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    selectedCollection,
    selectedCollectionSlug,
    selectedSource,
    selectedSourceSlug,
  ]);

  useEffect(() => {
    async function fetchMetrics() {
      if (!selectedSource?.id) {
        setIsLoadingMetrics(false);
        return;
      }

      setIsLoadingMetrics(true);
      setError(null);

      try {
        const response = await zeusSdk.sources.getMetricsTimeseries(selectedSource.id);
        setMetricsTS(response);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching metrics:', err);
        setError(err.message || 'Failed to fetch metrics data');
        setMetricsTS(null);
      } finally {
        setIsLoadingMetrics(false);
      }
    }

    void fetchMetrics();
  }, [selectedSource?.id]);

  if (isLoadingMetrics) {
    return <Loader />;
  }

  return <MetricsGraphs metricsTS={metricsTS} error={error} />;
}

export default SourceGraphs;
